import { all, select, put, call } from 'redux-saga/effects';
import i18n from 'i18next';
import { bufferedHandler, concurrentHandler, evaluate, callback } from '../../tools/effects';
import { getAcquisitionParameters, getImageryIdentifiers } from '../../../selectors';
import { ee } from '../../../services/earth-engine';
import { windowMissionAvailability, acquireDayMosaic } from '../../../algorithms/satellite/shoreSAvailability';
import { createThumbnail, createThumbnailCSqueeze, generateLayer } from '../../../algorithms/imagery';
import { generateVisualizationParams } from '../../../algorithms/utils';
import { Actions as Imagery } from '../imagery';
import { Types, Actions } from './header';
import { subtractIntervals } from './ranges';

/**
 * @function buildGeometry
 * @description Build the Shore-S ROI geometry. The Shore-S flow has no AOIChooser, so the
 * ROI is derived from the bounding box of the parsed `.mat` model's
 * coastlines (computed once in `parseShoreSModelFile` and stored in the
 * `shoreSModel` duck — not sessionStorage, since that bbox is produced
 * alongside a potentially multi-MB GeoJSON payload that already lives there).
 * @param {Array<number>} bbox - `[west, south, east, north]`.
 * **Called by:
 * @see {handleLoadImagesInRange}
 * @see {handleLoadThumbnails}
 * @see {handleAcquireImage}
 * @memberof module:ShoreS
 * @returns {Object|null} ee.Geometry, or null if bbox is invalid.
 */
const buildGeometry = (bbox) => {
  if (!Array.isArray(bbox) || bbox.length !== 4) return null;
  return ee.Geometry.Rectangle(bbox);
};

/**
 * @function selectMissions
 * @description Same TOA/Surface-Reflectance mission list selection used everywhere else in Shore-S.
 * @param {Object} satellite - `satellite` slice of the acquisition state.
 * **Called by:
 * @see {handleLoadImagesInRange}
 * @see {handleLoadThumbnails}
 * @memberof module:ShoreS
 * @returns {Array} The selected mission list.
 */
const selectMissions = (satellite) => {
  const type = window.sessionStorage.getItem('typeSatellite');
  return type === null || type === 'TOA' ? satellite.missions : satellite.surfaceReflectance;
};

/**
 * @function featuresToDayMap
 * @description Reduce a FeatureCollection result to a `{ dayISO: cloud }` dictionary. The
 * query already returns one mosaicked feature per day, so this is effectively
 * 1:1; the max guard only protects against any incidental duplicate.
 * @param {Object} fc - ee-evaluated FeatureCollection from `windowMissionAvailability`.
 * @param {Object} [into] - Existing `{ dayISO: cloud }` dictionary to merge into.
 * **Called by:
 * @see {handleLoadImagesInRange}
 * @memberof module:ShoreS
 * @returns {Object} `{ dayISO: cloud }` dictionary.
 */
const featuresToDayMap = (fc, into = {}) => {
  const features = (fc && fc.features) || [];
  features.forEach((f) => {
    const props = f.properties || {};
    if (props.dt == null) return;
    const day = new Date(props.dt).toISOString().slice(0, 10);
    const cloud = props.cloud != null ? props.cloud : 0;
    if (!(day in into) || cloud > into[day]) into[day] = cloud;
  });
  return into;
};

/**
 * @function handleLoadImagesInRange
 * @description Fetches image availability (per-day cloud fraction) for the requested
 * `[start, end]` period, one mission/gap at a time, merging results into the incremental cache.
 * Only the portions of the range not already covered by `cachedRanges` (see `subtractIntervals`)
 * are actually queried.
 * @param {Object} action - Redux action, with `payload.start`/`payload.end` (ISO dates).
 * **Called by:
 * @see {LOAD_IMAGES_IN_RANGE}
 * @memberof module:ShoreS
 * @returns {void}
 */
const handleLoadImagesInRange = function* ({ payload: { start, end } }) {
  // Signal loading immediately so the UI never shows the empty state
  // while waiting for the debounce / geometry check to complete.
  yield put(Actions.setLoading(true));

  const { satellite } = yield select(getAcquisitionParameters);
  const roiBBox = yield select((s) => s.shoreSModel.roiBBox);
  const geometry = buildGeometry(roiBBox);

  if (!geometry) {
    console.warn('[ShoreS] ROI bbox not available — upload and parse a model output file first.');
    yield put(Actions.setError('no-roi'));
    return;
  }

  if (!satellite) {
    yield put(Actions.setLoading(false));
    return;
  }

  const cachedRanges = yield select((s) => s.shoreS.cachedRanges);
  const gaps = subtractIntervals([start, end], cachedRanges);

  // Everything requested is already cached — no fetch needed.
  if (gaps.length === 0) {
    yield put(Actions.setLoading(false));
    return;
  }

  const missions = selectMissions(satellite);

  // mission.name -> { name, shortname, dates }
  const accumulated = {};

  for (const [gapStart, gapEnd] of gaps) {
    for (const mission of missions) {
      try {
        const fc = yield evaluate(windowMissionAvailability(mission, geometry, gapStart, gapEnd));
        if (!accumulated[mission.name]) {
          accumulated[mission.name] = { name: mission.name, shortname: mission.shortname, dates: {} };
        }
        featuresToDayMap(fc, accumulated[mission.name].dates);
      } catch (e) {
        console.error('[ShoreS] window fetch failed', mission.name, gapStart, gapEnd, e);
      }
    }
  }

  yield put(Actions.setRangeResult(Object.values(accumulated), gaps));
};

/**
 * @function fetchOneThumbnail
 * @description Fetches a GEE thumbnail URL for one image, mirroring `acquisition`'s
 * `fetchOneThumbnail` — but scoped to whatever image list the caller
 * passes in (the image-selection screen's currently displayed candidates,
 * not the full `availableDates` cache), and using the Shore-S ROI
 * (`roiBBox`) instead of a user-drawn AOI geometry.
 * @param {{name: string, date: string, id: *, content: number}} entry
 * @param {number} index - Fallback id when `entry.id` is unset.
 * @param {Array} missions - Mission list to resolve `entry.name` against.
 * @param {Object} geometry - ee.Geometry (Shore-S ROI).
 * @param {Function} thumbnailFn - `createThumbnail` or `createThumbnailCSqueeze`.
 * **Called by:
 * @see {thumbnailWorker}
 * @memberof module:ShoreS
 * @returns {void}
 */
function* fetchOneThumbnail(entry, index, missions, geometry, thumbnailFn) {
  try {
    const mission = missions.find((m) => m.name === entry.name);
    if (!mission) return;

    // NOT `mission.algorithms.acquire` — see `acquireDayMosaic`'s doc comment
    // for why the shared single-image function is incompatible with Shore-S's
    // day-only (no exact timestamp) date entries.
    const image = acquireDayMosaic(mission, entry.date, geometry);
    const visParam = generateVisualizationParams(mission);
    const url = yield callback(thumbnailFn, image, geometry, visParam);

    yield put(
      Actions.insertMetadata(entry.name, entry.date, {
        id: entry.id ?? index,
        thumbnail: url,
        clouds: entry.content,
      }),
    );
  } catch (e) {
    console.error('[ShoreS] thumbnail load failed', entry?.name, entry?.date, e);
  }
}

const THUMBNAIL_WORKER_COUNT = 6;

/**
 * @function thumbnailWorker
 * @description One worker of the thumbnail-loading pool: pulls entries off the shared `queue`
 * until it's empty. Several of these run concurrently (see `handleLoadThumbnails`) so a single
 * slow request doesn't hold up thumbnails that already finished.
 * @param {Array<{entry, index}>} queue - Shared mutable work queue.
 * @param {Array} missions
 * @param {Object} geometry - ee.Geometry (Shore-S ROI).
 * @param {Function} thumbnailFn
 * **Called by:
 * @see {handleLoadThumbnails}
 * @memberof module:ShoreS
 * @returns {void}
 */
function* thumbnailWorker(queue, missions, geometry, thumbnailFn) {
  while (queue.length) {
    const { entry, index } = queue.shift();
    yield call(fetchOneThumbnail, entry, index, missions, geometry, thumbnailFn);
  }
}

/**
 * @function handleLoadThumbnails
 * @description Fetches GEE thumbnail URLs for the given images, skipping any already present in
 * `state.shoreS.metadata`, via a worker-pool of `THUMBNAIL_WORKER_COUNT` concurrent requests
 * (see `thumbnailWorker`) instead of one-at-a-time or lockstep-batched.
 * @param {Object} action - Redux action, with `payload.images`.
 * **Called by:
 * @see {LOAD_THUMBNAILS}
 * @memberof module:ShoreS
 * @returns {void}
 */
const handleLoadThumbnails = function* ({ payload: { images } }) {
  const { satellite } = yield select(getAcquisitionParameters);
  const roiBBox = yield select((s) => s.shoreSModel.roiBBox);
  const geometry = buildGeometry(roiBBox);

  if (!geometry || !satellite || !images?.length) return;
  const metadata = yield select((s) => s.shoreS.metadata);
  const alreadyFetched = new Set(metadata.map((m) => `${m.date}-${m.missionName}`));

  const missions = selectMissions(satellite);
  const thumbnailFn = window.location.href.indexOf('csqueeze') === -1 ? createThumbnail : createThumbnailCSqueeze;

  const queue = images
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => !alreadyFetched.has(`${entry.date}-${entry.name}`));

  const workerCount = Math.min(THUMBNAIL_WORKER_COUNT, queue.length);
  yield all(Array.from({ length: workerCount }, () => call(thumbnailWorker, queue, missions, geometry, thumbnailFn)));
};

/**
 * @function handleAcquireImage
 * @description Loads one image as a raster layer on the map, mirroring `acquisition`'s
 * `handleAcquireImage` (same Landsat scale-factor/visualization logic, same
 * `Imagery.pushImage`/`Imagery.loadLayer` sink so `LoadedImagesAccordion`
 * renders it identically) — but sourcing the clip geometry from the Shore-S
 * ROI (`shoreSModel.roiBBox`) and acquiring via `acquireDayMosaic` (Shore-S
 * dates are day-only) instead of `mission.algorithms.acquire`.
 * @param {Object} action - Redux action, with `payload.missionName`/`payload.date`.
 * **Called by:
 * @see {ACQUIRE_IMAGE}
 * @memberof module:ShoreS
 * @returns {void}
 */
const handleAcquireImage = function* ({ payload: { missionName, date } }) {
  const { satellite } = yield select(getAcquisitionParameters);
  const roiBBox = yield select((s) => s.shoreSModel.roiBBox);
  const geometry = buildGeometry(roiBBox);
  const { imageId } = yield select(getImageryIdentifiers);

  if (!geometry || !satellite) return;

  const mission = satellite.get(missionName);
  let image = acquireDayMosaic(mission, date, geometry);

  let layerParams;
  const missionInfo = missionName.split('/');
  if (missionInfo[0] === 'LANDSAT') {
    let bands = [mission.bands.red, mission.bands.green, mission.bands.blue];
    let visualization = {
      bands: bands,
      min: 0.0,
      max: 0.4,
    };
    image = image.visualize(visualization);
    image = image.rename(bands).clip(geometry);
    // `visualize` above already stretched the image to 8-bit RGB (0-255), so the
    // layer must not inherit the mission's own reflectance min/max on top of it —
    // that would clip everything above 0.4 to white.
    layerParams = { bands };
  }

  const layer = generateLayer(image, mission, i18n.t('forms.imageryOverlay.base'), layerParams);
  yield put(Imagery.pushImage(mission.shortname + '/' + date, date, mission.name));
  yield put(Imagery.loadLayer(layer, imageId));
};

const root = function* () {
  yield all([
    bufferedHandler(Types.LOAD_IMAGES_IN_RANGE, handleLoadImagesInRange),
    concurrentHandler(Types.LOAD_THUMBNAILS, handleLoadThumbnails),
    bufferedHandler(Types.ACQUIRE_IMAGE, handleAcquireImage),
  ]);
};

export default root;
